import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import API_BASE_URL from '../../../../Constants';

/**
 * A nested component that shows:
 * 1) Agency dropdown
 * 2) Agency Tests (based on selected agency)
 * 3) Intersection level (BUILDING/CUSTOMER/MGMTCO/SERVICECO)
 * 4) Accounts list from the chosen level
 *
 * Then it sets those picks back into the parent formData or new state fields.
 */
function CreateWizardSection({
  selectedAgencyId,
  setSelectedAgencyId,
  selectedTestId,
  setSelectedTestId,
  intersectionLevel,
  setIntersectionLevel,
  selectedAccountId,
  setSelectedAccountId,
}) {
  const [agencies, setAgencies] = useState([]);
  const [agencyTests, setAgencyTests] = useState([]);

  const [accounts, setAccounts] = useState([]);

  // On mount => fetch all agencies
  useEffect(() => {
    fetchAllAgencies();
  }, []);

  // If selectedAgencyId changes => fetch that agency’s tests
  useEffect(() => {
    if (selectedAgencyId) {
      fetchAgencyTests(selectedAgencyId);
    } else {
      setAgencyTests([]);
      setSelectedTestId('');
    }
  }, [selectedAgencyId]);

  // If intersectionLevel changes => fetch that type of accounts
  useEffect(() => {
    fetchAccountsForLevel(intersectionLevel);
    setSelectedAccountId('');
  }, [intersectionLevel]);

  // ---------- Fetch All Agencies ----------
  const fetchAllAgencies = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/inspections/agency/agencies`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`Agency fetch error. Status ${res.status}`);
      }
      const data = await res.json();
      setAgencies(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setAgencies([]);
    }
  };

  // ---------- Fetch Tests for one Agency ----------
  const fetchAgencyTests = async (agencyId) => {
    try {
      const url = `${API_BASE_URL}/api/inspections/agency/agencyTestsForAgency/${agencyId}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        throw new Error(`Agency tests fetch error. Status ${res.status}`);
      }
      const data = await res.json();
      setAgencyTests(Array.isArray(data) ? data : []);
      setSelectedTestId(''); // reset
    } catch (err) {
      console.error(err);
      setAgencyTests([]);
    }
  };

  // ---------- Fetch Accounts for the chosen level ----------
  const fetchAccountsForLevel = async (lvl) => {
    let url = '';
    switch (lvl) {
      case 'BUILDING':
        // You might have an endpoint like /api/setup/buildings/all
        url = `${API_BASE_URL}/api/setup/buildings/all`;
        break;
      case 'CUSTOMER':
        url = `${API_BASE_URL}/api/setup/customers/all`;
        break;
      case 'MGMTCO':
        url = `${API_BASE_URL}/api/setup/managementcompanies/all`;
        break;
      case 'SERVICECO':
        url = `${API_BASE_URL}/api/setup/servicecompanies/all`;
        break;
      default:
        url = '';
    }
    if (!url) {
      setAccounts([]);
      return;
    }

    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        throw new Error(`Fetch ${lvl} error. Status ${res.status}`);
      }
      const data = await res.json();
      setAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setAccounts([]);
    }
  };

  return (
    <div className="space-y-4 p-2 border rounded bg-gray-50">
      {/* Agency dropdown */}
      <div>
        <label className="block font-semibold mb-1">Agency:</label>
        <select
          className="border p-2 w-full"
          value={selectedAgencyId}
          onChange={(e) => setSelectedAgencyId(e.target.value)}
        >
          <option value="">--Select Agency--</option>
          {agencies.map((ag) => (
            <option key={ag.agencyId} value={ag.agencyId}>
              {ag.agencyName} (#{ag.agencyId})
            </option>
          ))}
        </select>
      </div>

      {/* Agency Test dropdown */}
      <div>
        <label className="block font-semibold mb-1">Test:</label>
        <select
          className="border p-2 w-full"
          value={selectedTestId}
          onChange={(e) => setSelectedTestId(e.target.value)}
        >
          <option value="">--Select Test--</option>
          {agencyTests.map((t) => (
            <option key={t.testId} value={t.testId}>
              {t.label} (#{t.testId})
            </option>
          ))}
        </select>
      </div>

      {/* Intersection level */}
      <div>
        <label className="block font-semibold mb-1">Account Level:</label>
        <select
          className="border p-2 w-full"
          value={intersectionLevel}
          onChange={(e) => setIntersectionLevel(e.target.value)}
        >
          <option value="BUILDING">Building</option>
          <option value="CUSTOMER">Customer</option>
          <option value="MGMTCO">Management</option>
          <option value="SERVICECO">Service Co</option>
        </select>
      </div>

      {/* Account dropdown */}
      <div>
        <label className="block font-semibold mb-1">Account:</label>
        <select
          className="border p-2 w-full"
          value={selectedAccountId}
          onChange={(e) => setSelectedAccountId(e.target.value)}
        >
          <option value="">--Select {intersectionLevel}--</option>
          {accounts.map((acct) => {
            // We'll guess a label
            const label =
              acct.buildingReference ||
              acct.companyName ||
              acct.customerName ||
              `(ID#${acct.id})`;

            // We'll guess an ID field
            const value =
              acct.buildingID ||
              acct.managementId ||
              acct.customerID ||
              acct.serviceCompanyId ||
              acct.id;

            return (
              <option key={value} value={value}>
                {label} (#{value})
              </option>
            );
          })}
        </select>
      </div>
    </div>
  );
}

/**
 * The main modal 
 * “ViewAccountPricingModal” plus the embedded “CreateWizardSection”
 */
export default function ViewAccountPricingModal({
  open,
  onClose,
  pricingId,
  refreshList,
  setNext,
  setPrev,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // If there's no "pricingId," we assume "create mode." If we have an ID, we do "view/edit" mode.
  const [isCreateMode, setIsCreateMode] = useState(!pricingId);
  // For an existing record, you can toggle editing
  const [isEditMode, setIsEditMode] = useState(false);

  const [loading, setLoading] = useState(false);

  // The main form data for the "account_pricing" row
  const [formData, setFormData] = useState({
    dateFrom: '',
    dateTo: '',
    amount: '',
    active: false,
    // if you store agency/test in a single text form, you can do so,
    // but we want them separate now => see next states
  });

  // The separate states for the wizard
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const [selectedTestId, setSelectedTestId] = useState('');
  const [intersectionLevel, setIntersectionLevel] = useState('BUILDING');
  const [selectedAccountId, setSelectedAccountId] = useState('');

  // 1) If we are opening an existing record => fetch it
  useEffect(() => {
    if (!open) return;

    if (pricingId) {
      setIsCreateMode(false);
      fetchExistingRecord(pricingId);
    } else {
      // no ID => create
      setIsCreateMode(true);
      clearForm();
    }
  }, [open, pricingId]);

  const fetchExistingRecord = async (id) => {
    setLoading(true);
    try {
      // fetch the existing row
      const res = await fetch(
        `${API_BASE_URL}/api/inspections/account-pricing/${id}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        throw new Error(`Error fetching #${id}, status ${res.status}`);
      }
      const data = await res.json();
      setFormData({
        dateFrom: data.dateFrom || '',
        dateTo: data.dateTo || '',
        amount: data.amount || '',
        active: !!data.active,
      });

      // We also see if data had “agencyId, testId, level, accountId” 
      // or if the backend responded with "agencyName/testLabel"
      // We'll guess we can do:
      setSelectedAgencyId(data.agencyId ? String(data.agencyId) : '');
      setSelectedTestId(data.testId ? String(data.testId) : '');
      setIntersectionLevel(data.level || 'BUILDING');
      // we might not have the “account ID” from the backend
      setSelectedAccountId(''); // or some existing ID if the backend sent it

      setIsCreateMode(false);
      setIsEditMode(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 2) close / expand
  const handleClose = () => {
    if (isEditMode && !isCreateMode) {
      const confirmClose = window.confirm('Discard changes?');
      if (!confirmClose) return;
    }
    onClose();
  };
  const toggleExpand = () => setIsExpanded(!isExpanded);

  // 3) clear form => create mode
  const clearForm = () => {
    setFormData({
      dateFrom: '',
      dateTo: '',
      amount: '',
      active: false,
    });
    setSelectedAgencyId('');
    setSelectedTestId('');
    setIntersectionLevel('BUILDING');
    setSelectedAccountId('');
    setIsCreateMode(true);
    setIsEditMode(true);
  };

  // 4) handleChange for dateFrom/dateTo/amount/active
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: val }));
  };

  // 5) Save => if create => do the 2-step
  const handleSave = async () => {
    if (isCreateMode) {
      await createNewPricing();
    } else {
      await updateExistingPricing();
    }
  };

  const createNewPricing = async () => {
    // ensure user picked an agency/test + intersection
    if (!selectedAgencyId) {
      alert('Please pick an Agency');
      return;
    }
    if (!selectedTestId) {
      alert('Please pick a Test');
      return;
    }
    if (!selectedAccountId) {
      alert(`Please pick an account from ${intersectionLevel}`);
      return;
    }

    try {
      // Step A) POST main row
      // We'll assume the backend wants "agencyId, testId"
      const mainPayload = {
        agencyId: parseInt(selectedAgencyId, 10),
        testId: parseInt(selectedTestId, 10),
        dateFrom: formData.dateFrom,
        dateTo: formData.dateTo,
        amount: parseFloat(formData.amount || '0') || 0,
        active: !!formData.active,
        level: intersectionLevel, // if you store it
      };

      const createRes = await fetch(`${API_BASE_URL}/api/inspections/account-pricing`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mainPayload),
      });
      if (!createRes.ok) {
        throw new Error(`Create main row failed. Status ${createRes.status}`);
      }
      const createdData = await createRes.json();
      const newId = createdData.accountPricingId;

      // Step B) intersection
      let intersectionUrl = '';
      let intersectionPayload = { accountPricingId: newId };

      switch (intersectionLevel) {
        case 'BUILDING':
          intersectionUrl = `${API_BASE_URL}/api/inspections/account-pricing-building`;
          intersectionPayload.buildingId = parseInt(selectedAccountId, 10);
          break;
        case 'CUSTOMER':
          intersectionUrl = `${API_BASE_URL}/api/inspections/account-pricing-customer`;
          intersectionPayload.customerId = parseInt(selectedAccountId, 10);
          break;
        case 'MGMTCO':
          intersectionUrl = `${API_BASE_URL}/api/inspections/account-pricing-management`;
          intersectionPayload.managementId = parseInt(selectedAccountId, 10);
          break;
        case 'SERVICECO':
          intersectionUrl = `${API_BASE_URL}/api/inspections/account-pricing-serviceco`;
          intersectionPayload.serviceCompanyId = parseInt(selectedAccountId, 10);
          break;
        default:
          intersectionUrl = null;
      }

      if (intersectionUrl) {
        const intersectionRes = await fetch(intersectionUrl, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(intersectionPayload),
        });
        if (!intersectionRes.ok) {
          throw new Error(`Create intersection row failed. Status ${intersectionRes.status}`);
        }
      }

      alert(`Created new account pricing #${newId}`);
      if (refreshList) refreshList();
      onClose();
    } catch (err) {
      console.error(err);
      alert(`Error: ${err.message}`);
    }
  };

  const updateExistingPricing = async () => {
    // If the intersection is already set, we might just do a PUT on the main row
    try {
      const payload = {
        // if your backend wants "agencyId/testId" for update, you'd do that too
        dateFrom: formData.dateFrom,
        dateTo: formData.dateTo,
        amount: parseFloat(formData.amount || '0') || 0,
        active: !!formData.active,
      };

      const res = await fetch(
        `${API_BASE_URL}/api/inspections/account-pricing/${pricingId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        throw new Error(`Update failed. Status ${res.status}`);
      }
      alert('Updated successfully');
      setIsEditMode(false);
      if (refreshList) refreshList();
    } catch (err) {
      console.error(err);
      alert(`Failed to update: ${err.message}`);
    }
  };

  // 6) Delete
  const handleDelete = async () => {
    if (!window.confirm('Are you sure?')) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/inspections/account-pricing/${pricingId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      if (!res.ok) {
        throw new Error(`Delete fail. Status ${res.status}`);
      }
      alert('Deleted');
      onClose();
      if (refreshList) refreshList();
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  // For display in the header
  const displayedId = isCreateMode ? 'NEW' : pricingId;

  // If not open
  if (!open) return null;

  return (
    <>
      {/* Next/Prev arrows if viewing existing record (not create, not editing) */}
      {open && !isCreateMode && !isEditMode && (
        <>
          <button
            onClick={setPrev}
            style={{
              position: 'fixed',
              left: `calc(50% - ${isExpanded ? '45%' : '500px'} - 60px)`,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1500,
            }}
            className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
          >
            <ArrowBackIcon />
          </button>
          <button
            onClick={setNext}
            style={{
              position: 'fixed',
              right: `calc(50% - ${isExpanded ? '45%' : '500px'} - 60px)`,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1500,
            }}
            className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
          >
            <ArrowForwardIcon />
          </button>
        </>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          style: {
            width: isExpanded ? '90%' : '1000px',
            maxHeight: '90vh',
            minHeight: '90vh',
          },
        }}
        fullWidth
        style={{ zIndex: 1400 }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center p-4">
            <div className="flex items-center space-x-4 flex-grow">
              <button
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-gray-200 transition"
                aria-label="Close Modal"
              >
                <ArrowBackIcon />
              </button>
              <h2 className="text-2xl font-bold text-left">
                {isCreateMode
                  ? `Create New Account Pricing`
                  : isEditMode
                  ? `Edit Account Pricing #${displayedId}`
                  : `View Account Pricing #${displayedId}`}
              </h2>
            </div>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label="Expand"
            >
              {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 p-4">
            {isCreateMode ? (
              <>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-gray-300 text-black font-bold rounded-md hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-green-600 text-white font-bold rounded-md hover:bg-green-700 transition"
                >
                  Save (Create)
                </button>
              </>
            ) : isEditMode ? (
              <>
                <button
                  onClick={() => setIsEditMode(false)}
                  className="px-4 py-2 bg-gray-300 text-black font-bold rounded-md hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-md hover:bg-yellow-600 transition"
                >
                  Save
                </button>
                <button
                  onClick={handleDelete}
                  className="px-6 py-2 bg-red-500 text-white font-bold rounded-md hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditMode(true)}
                  className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-md hover:bg-yellow-600 transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    clearForm();
                  }}
                  className="px-6 py-2 bg-blue-500 text-white font-bold rounded-md hover:bg-blue-600 transition"
                >
                  Clear (New)
                </button>
              </>
            )}
          </div>
        </div>

        <DialogContent dividers>
          {loading ? (
            <p>Loading account pricing data...</p>
          ) : (
            <div className="space-y-4">
              {isCreateMode ? (
                // Show the "CreateWizardSection" to pick agency/test/account
                <>
                  <CreateWizardSection
                    selectedAgencyId={selectedAgencyId}
                    setSelectedAgencyId={setSelectedAgencyId}
                    selectedTestId={selectedTestId}
                    setSelectedTestId={setSelectedTestId}
                    intersectionLevel={intersectionLevel}
                    setIntersectionLevel={setIntersectionLevel}
                    selectedAccountId={selectedAccountId}
                    setSelectedAccountId={setSelectedAccountId}
                  />

                  {/* dateFrom/dateTo/amount/active part */}
                  <div>
                    <label className="block font-semibold mb-1">Date From:</label>
                    <input
                      type="date"
                      name="dateFrom"
                      className="w-full p-2 border"
                      value={formData.dateFrom}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-1">Date To:</label>
                    <input
                      type="date"
                      name="dateTo"
                      className="w-full p-2 border"
                      value={formData.dateTo}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-1">Amount:</label>
                    <input
                      type="number"
                      step="0.01"
                      name="amount"
                      className="w-full p-2 border"
                      value={formData.amount}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="font-semibold">Active?</label>
                    <input
                      type="checkbox"
                      name="active"
                      checked={!!formData.active}
                      onChange={handleFormChange}
                    />
                  </div>
                </>
              ) : isEditMode ? (
                // Editing an existing record
                <>
                  <div>
                    <label className="block font-semibold mb-1">Date From:</label>
                    <input
                      type="date"
                      name="dateFrom"
                      className="w-full p-2 border"
                      value={formData.dateFrom}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-1">Date To:</label>
                    <input
                      type="date"
                      name="dateTo"
                      className="w-full p-2 border"
                      value={formData.dateTo}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-1">Amount:</label>
                    <input
                      type="number"
                      step="0.01"
                      name="amount"
                      className="w-full p-2 border"
                      value={formData.amount}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="font-semibold">Active?</label>
                    <input
                      type="checkbox"
                      name="active"
                      checked={!!formData.active}
                      onChange={handleFormChange}
                    />
                  </div>
                </>
              ) : (
                // Just viewing
                <>
                  <p>Date From: {formData.dateFrom || 'N/A'}</p>
                  <p>Date To: {formData.dateTo || 'N/A'}</p>
                  <p>Amount: {formData.amount || 'N/A'}</p>
                  <p>Active? {formData.active ? 'Yes' : 'No'}</p>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
